import { useRef, useEffect, useState } from 'react';
import {
  handleMLChange,
  handleRemoveML,
  handleAddML,
  divertOptions,
} from '../function/HandleMLChange';
import ValidateMLValue from '../function/ValidateMLValue';
import InfoButton from './InfoButton';
import CustomSelect from '../inputs/CustomSelect';
import { UnitConversion } from '../inputs/UnitConversion';

function MultiLevel({ inputs, setInputs, options, result }) {
  const [showTooltip, setShowTooltip] = useState(false);
  if (
    options.filter((option) => option.field === 'connection_conveyor')
      .length === 0
  ) {
    return null;
  }

  const { divert_options } = divertOptions(options);
  const divert_options_choices =
    inputs.ml_connection === 'mounting kit(s)'
      ? ['No additional option']
      : [
          'No additional option',
          ...divert_options.map((option) => option.name),
        ];

  const showDivertOption =
    divert_options.length > 0 &&
    inputs.ml_connection !== 'mounting kit(s)' &&
    inputs.connection_conveyor?.some(
      (conveyor) => conveyor.direction === 'divert'
    );

  const hasMountingKit = options.some(
    (option) => option.field === 'nbr_ml_mounting_kit'
  );
  const hasMergeBridge = options.some(
    (option) => option.field === 'nbr_merge_connection_bridge'
  );
  const hasDivertBridge = options.some(
    (option) => option.field === 'nbr_divert_connection_bridge'
  );

  let direction_options = [];
  if (hasMountingKit) {
    direction_options = ['divert', 'merge'];
  } else {
    if (hasMergeBridge) direction_options.push('merge');
    if (hasDivertBridge) direction_options.push('divert');
  }
  if (direction_options.length === 1) {
    inputs.connection_conveyor?.forEach((element, index) => {
      if (element.direction !== direction_options[0])
        handleMLChange(
          index,
          'direction',
          direction_options[0],
          setInputs,
          options
        );
    });
  }

  const column_list = [
    {
      field: 'winding',
      name: 'Winding',
      input: 'select',
      select_options: Array.from(
        { length: parseInt(result.nbr_windings, 10) + 1 },
        (_, index) => index
      ),
    },
    {
      field: 'direction',
      name: 'Direction',
      input: 'select',
      select_options: direction_options,
    },
    {
      field: 'divert_option',
      name: 'Divert Option',
      input: 'select',
      select_options: divert_options_choices,
    },
    {
      field: 'angle',
      name: 'Angle [°]',
      input: 'float_field',
    },
  ];

  const renderField = (column_list, conveyor, index) => {
    const { field, input, select_options } = column_list;

    if (field === 'divert_option' && conveyor.direction !== 'divert') {
      return <td className="table-value"></td>;
    }

    let renderInput;
    if (input === 'select') {
      const isLastWinding =
        (conveyor.winding === parseInt(result.nbr_windings, 10) &&
          inputs.transport_direction === 'transport up') ||
        (conveyor.winding === 1 &&
          inputs.transport_direction === 'transport down');
      const filteredOptions = isLastWinding
        ? select_options
        : select_options.filter((option) => option !== 'Static arm');

      renderInput = (
        <div className="input">
          <CustomSelect
            value={conveyor[field]}
            onChange={(value) =>
              handleMLChange(index, field, value, setInputs, options)
            }
            optionChoice={filteredOptions}
          />
        </div>
      );
    } else if (input === 'float_field') {
      const { warning } = ValidateMLValue(field, conveyor[field]);

      renderInput = (
        <div className="input">
          <input
            type="number"
            name={field}
            value={conveyor[field]}
            onChange={(e) =>
              handleMLChange(index, field, e.target.value, setInputs, options)
            }
          />
          {warning !== '' && <InfoButton infoText={warning} type="warning" />}
        </div>
      );
    }

    return (
      <td className="table-value" key={field}>
        {renderInput}
      </td>
    );
  };

  const system = inputs.unit_system;
  const unit = UnitConversion(null, system, 'mm').displayedUnit;

  const height =
    result.connection_conveyor_height
      ?.sort((a, b) => a.angle - b.angle)
      ?.sort((a, b) => a.winding - b.winding)
      ?.map((conv) =>
        Math.round(UnitConversion(conv.height, system, 'mm').displayedValue)
      ) || Array(inputs.connection_conveyor?.length).fill(null);

  return (
    <div className="ml-container-wrapper">
      <div className="glass ml-table-container">
        {inputs.connection_conveyor?.length > 0 && (
          <table>
            <thead>
              <tr>
                {column_list
                  .filter(
                    (fieldInfo) =>
                      fieldInfo.field !== 'divert_option' || showDivertOption
                  )
                  .map((fieldInfo) => (
                    <th key={fieldInfo.field}>{fieldInfo.name}</th>
                  ))}
                <th>Height [{unit}]</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {inputs.connection_conveyor
                .sort((a, b) => a.angle - b.angle)
                .sort((a, b) => a.winding - b.winding)
                .map((connection, index) => (
                  <tr key={index}>
                    {column_list
                      .filter(
                        (fieldInfo) =>
                          fieldInfo.field !== 'divert_option' ||
                          showDivertOption
                      )
                      .map((fieldInfo) =>
                        renderField(fieldInfo, connection, index)
                      )}
                    <td className="table-value">{height[index]}</td>
                    <td className="remove-cell">
                      <button
                        className="remove-button button-warning"
                        onMouseEnter={() => setShowTooltip(index)}
                        onMouseLeave={() => setShowTooltip(null)}
                        onClick={() => {
                          setShowTooltip(null);
                          handleRemoveML(index, setInputs);
                        }}
                      >
                        ×
                      </button>
                      {showTooltip === index && (
                        <div className="tooltip warning right">
                          Remove connection
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        )}
      </div>
      <button
        className="button-md button-primary"
        onClick={() => handleAddML(setInputs, result)}
      >
        <span>+</span>Add connection
      </button>
    </div>
  );
}

export function CircleWithTangents({ inputs, result, radius = 75 }) {
  const canvasRef = useRef(null);
  const reverseConfig = !['A', 'C', 'E', 'G', 'L'].includes(
    inputs.configuration
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !inputs.connection_conveyor) return;
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    const radius_inside =
      (radius * result.column_centre_to_inside_windings * 2) /
      result.footprint_diameter;

    const baseDrawing = [
      {
        angle: 270,
        direction: reverseConfig ? 0 : 180,
        color: '#012650',
        width: 1,
      },
    ];
    const tangents = [
      ...baseDrawing,
      ...inputs.connection_conveyor.map((obj) => {
        const isReversed = reverseConfig
          ? (obj.direction === 'merge' &&
              inputs.transport_direction === 'transport up') ||
            (obj.direction === 'divert' &&
              inputs.transport_direction === 'transport down')
          : (obj.direction === 'merge' &&
              inputs.transport_direction === 'transport down') ||
            (obj.direction === 'divert' &&
              inputs.transport_direction === 'transport up');

        return {
          angle: (reverseConfig ? -1 : 1) * obj.angle + 270,
          direction: isReversed ? 0 : 180,
          color: 'green',
          width: 3,
        };
      }),
    ];

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    tangents.forEach(({ angle, direction, color, width }) =>
      DrawTangent(
        ctx,
        angle,
        direction,
        color,
        width,
        radius,
        centerX,
        centerY,
        radius_inside
      )
    );

    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI, false); // Outer circle, clockwise
    ctx.arc(centerX, centerY, radius_inside, 0, 2 * Math.PI, true); // Inner circle, counterclockwise
    ctx.closePath();
    ctx.lineWidth = 1;
    ctx.fillStyle = '#bfbfbf';
    ctx.strokeStyle = '#012650';
    ctx.stroke();
    ctx.fill();

    DrawTangent(
      ctx,
      (reverseConfig ? -1 : 1) * result.configuration_angle + 270,
      reverseConfig ? 180 : 0,
      '#012650',
      1,
      radius,
      centerX,
      centerY,
      radius_inside
    );
  }, [radius, inputs, result, reverseConfig]);

  return <canvas ref={canvasRef} width={300} height={300} />;
}

function DrawTangent(
  ctx,
  angle,
  direction,
  color,
  width,
  radius,
  centerX,
  centerY,
  radius_inside
) {
  const tangentAngle = (angle * Math.PI) / 180;
  const tangentX = centerX + radius * Math.cos(tangentAngle);
  const tangentXinside = centerX + radius_inside * Math.cos(tangentAngle);
  const tangentY = centerY + radius * Math.sin(tangentAngle);
  const tangentYinside = centerY + radius_inside * Math.sin(tangentAngle);

  const tangentDirection =
    tangentAngle + Math.PI / 2 + (direction * Math.PI) / 180;

  const lineEndX = tangentX + radius * Math.cos(tangentDirection);
  const lineEndY = tangentY + radius * Math.sin(tangentDirection);
  const lineEndXinside = tangentXinside + radius * Math.cos(tangentDirection);
  const lineEndYinside = tangentYinside + radius * Math.sin(tangentDirection);

  ctx.beginPath();
  ctx.moveTo(tangentX, tangentY);
  ctx.lineTo(lineEndX, lineEndY);
  ctx.lineTo(lineEndXinside, lineEndYinside);
  ctx.lineTo(tangentXinside, tangentYinside);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
  ctx.closePath();
  ctx.fillStyle = '#bfbfbf';
  ctx.fill();

  return ctx;
}

export default MultiLevel;
